import { getAuthHeaders } from '@/hooks/useAuth';
import type { Reader, FreelancerRequest, BookingEligibility } from '@/types/reader';

const headers = () => getAuthHeaders();

export async function fetchPublicReaderProfile(readerId: string): Promise<Reader> {
  const res = await fetch(`/api/freelancer/reader/${readerId}/public-profile`);
  if (!res.ok) throw new Error('Reader not found');
  const data = await res.json();
  return data.reader || data;
}

export async function fetchAvailableReaders(): Promise<Reader[]> {
  const res = await fetch('/api/freelancer/readers', { headers: headers() });
  if (!res.ok) throw new Error('Failed to fetch readers');
  const data = await res.json();
  return data.readers || data;
}

export async function fetchReaderDetails(readerId: string): Promise<Reader> {
  const res = await fetch(`/api/freelancer/reader/${readerId}`, { headers: headers() });
  if (!res.ok) throw new Error('Failed to fetch reader details');
  const data = await res.json();
  return data.reader || data;
}

export async function sendReaderInvite(data: {
  readerId: string;
  sessionDate: string;
  sessionTime: string;
  timezone: string;
  locationType: string;
  serviceType: string;
  sessionLength: number;
  notes?: string;
  requesterFirstName: string;
  requesterLastName: string;
}): Promise<{ request: FreelancerRequest; message: string }> {
  const res = await fetch('/api/freelancer/reader/invite', {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to send invite');
  return res.json();
}

export async function getMyReaderBookings(userId?: string): Promise<FreelancerRequest[]> {
  const url = userId
    ? `/api/freelancer/reader/my-bookings?userId=${userId}`
    : '/api/freelancer/reader/my-bookings';
  const res = await fetch(url, { headers: headers() });
  if (!res.ok) throw new Error('Failed to fetch bookings');
  const data = await res.json();
  return data.bookings || data;
}

export async function getBookingsWithReader(readerId: string): Promise<{
  bookings: FreelancerRequest[];
  pendingInvites: FreelancerRequest[];
}> {
  const res = await fetch(`/api/freelancer/reader/${readerId}/bookings`, { headers: headers() });
  if (!res.ok) throw new Error('Failed to fetch bookings with reader');
  return res.json();
}

export async function checkBookingEligibility(): Promise<BookingEligibility> {
  const res = await fetch('/api/freelancer/reader/booking-eligibility', { headers: headers() });
  if (!res.ok) throw new Error('Failed to check eligibility');
  return res.json();
}

// (Removed: a casting-era confirmReaderBooking that posted to
// /api/freelancer/reader/confirm. That endpoint went away with the reader
// rip-out and nothing referenced this. The live confirm — which captures the
// talent's authorized payment — is defined further down.)

export async function addCommentToReaderBooking(requestId: string, content: string): Promise<any> {
  const res = await fetch(`/api/freelancer/reader/booking/${requestId}/comment`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ content }),
  });
  if (!res.ok) throw new Error('Failed to send message');
  return res.json();
}

export async function markCommentsAsRead(requestId: string): Promise<any> {
  const res = await fetch(`/api/freelancer/reader/booking/${requestId}/mark-read`, {
    method: 'PUT',
    headers: headers(),
  });
  if (!res.ok) throw new Error('Failed to mark as read');
  return res.json();
}

// --- Reader Stripe Connect ---

export async function startReaderConnectOnboarding(country?: string): Promise<{ url: string; accountId: string }> {
  const res = await fetch('/api/reader/connect/onboard', { method: 'POST', headers: { ...headers(), 'Content-Type': 'application/json' }, body: JSON.stringify({ country }) });
  if (!res.ok) throw new Error('Failed to start onboarding');
  return res.json();
}

export async function getReaderConnectStatus(): Promise<{
  connected: boolean;
  onboarded: boolean;
  accountId?: string;
  chargesEnabled?: boolean;
  payoutsEnabled?: boolean;
  country?: string;
}> {
  const res = await fetch('/api/reader/connect/status', { headers: headers() });
  if (!res.ok) throw new Error('Failed to check status');
  return res.json();
}

export async function getReaderConnectDashboard(): Promise<{ url: string }> {
  const res = await fetch('/api/reader/connect/dashboard', { headers: headers() });
  if (!res.ok) throw new Error('Failed to get dashboard');
  return res.json();
}

// --- Reader payout choice: Stripe or Hey Joe credit ---

export type ReaderPayoutMethod = { isReader: boolean; payoutMethod: 'stripe' | 'credit'; balanceCents: number; earnedCents: number };

export async function getReaderPayoutMethod(): Promise<ReaderPayoutMethod> {
  const res = await fetch('/api/reader/payout-method', { headers: headers() });
  if (!res.ok) throw new Error('Failed to load payout choice');
  return res.json();
}

export async function setReaderPayoutMethod(method: 'stripe' | 'credit'): Promise<{ ok: boolean; payoutMethod: string; creditedCents: number }> {
  const res = await fetch('/api/reader/payout-method', { method: 'POST', headers: { ...headers(), 'Content-Type': 'application/json' }, body: JSON.stringify({ method }) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed to update payout choice');
  return data;
}

export async function applyCreditToMembership(): Promise<{ ok: boolean; cents: number; balanceCents: number; cashableCents: number }> {
  const res = await fetch('/api/wallet/apply-to-membership', { method: 'POST', headers: { ...headers(), 'Content-Type': 'application/json' }, body: JSON.stringify({ all: true }) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Could not apply credit');
  return data;
}

// --- Paid Booking Checkout ---

export async function createBookingCheckout(requestId: string): Promise<{ checkoutUrl: string; sessionId: string }> {
  const res = await fetch(`/api/reader/booking/${requestId}/checkout`, {
    method: 'POST',
    headers: headers(),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to create checkout');
  }
  return res.json();
}

// --- Concierge matching ("book a reader for me") ---

export interface ConciergeRequestInput {
  serviceType: string;
  sessionLength: number;
  sessionDate: string;
  sessionTime: string;
  timezone: string;
  locationType?: string;
  position?: string;
  notes?: string;
  requesterFirstName?: string;
  requesterLastName?: string;
}

// Flat concierge price: talent pays this regardless of which reader claims it.
// Mirrors CONCIERGE_RATE_CENTS_PER_15MIN on the backend ($25 / 15 min).
export const CONCIERGE_RATE_PER_15MIN = 25;

export async function createConciergeCheckout(
  input: ConciergeRequestInput,
): Promise<{ checkoutUrl: string; sessionId: string; requestId: string }> {
  const res = await fetch('/api/reader/concierge/checkout', {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to start concierge request');
  }
  return res.json();
}

// --- Plus-owned reader bookings (new engine — single source of truth) ---
// Talent requests + prepays; the request broadcasts to the reader pool; the
// first eligible reader claims it; funds release to the reader on completion.

export interface ReaderBookingRequestInput {
  serviceType: string;
  sessionLength: number;
  sessionDate?: string;
  sessionTime?: string;
  timezone?: string;        // booker's IANA zone
  timezoneLabel?: string;   // coarse label (Eastern/Central/Mountain/Pacific)
  locationType?: string;
  position?: string;
  notes?: string;
  talentPhone?: string;
  requesterFirstName?: string;
  requesterLastName?: string;
}

export interface ReaderAcceptance {
  readerId: string;
  readerName: string;
  rateDollars: number;
  payoutCents: number;
  acceptedAt?: string;
}

export interface ReaderBooking {
  id: string;
  serviceType: string;
  sessionLength: number;
  sessionDate?: string | null;
  sessionTime?: string | null;
  timezone?: string | null;
  timezoneLabel?: string | null;
  locationType?: string;
  position?: string;
  notes?: string;
  status?: 'requesting' | 'charging' | 'pending_reader' | 'declined' | 'booked' | 'completed' | 'expired' | 'canceled' | 'refunded';
  acceptances?: ReaderAcceptance[];
  // Direct ask with a card on file: the first accept at or under rateDollars
  // books and charges. Absent on pick-and-pay requests.
  chargeOnAccept?: boolean;
  rateDollars?: number;
  readerRespondBy?: string;     // pending_reader: when the ask lapses
  reopenedAt?: string;          // came back to the pool after a decline/timeout
  direct?: boolean;             // the actor asked one named reader
  // Direct ask where the reader said no or went quiet. Nothing reopens on its own:
  // the hold stays and the actor picks "send to the other readers" or "cancel".
  needsActorChoice?: boolean;
  actorChoiceReason?: 'expired' | 'declined';
  holdCents?: number;           // authorized on the card at request time, captured on booking
  holdCarried?: boolean;        // requesting: the hold from a direct ask travels with it
  instantBook?: boolean;        // pool view: does "I'm available" book THIS reader outright
  totalCents?: number;
  readerPayoutCents?: number;   // pool view: THIS reader's payout
  talentName?: string | null;
  talentAvatar?: string | null;
  talentEmail?: string;
  talentPhone?: string | null;
  chosenReaderId?: string | null;
  chosenReaderName?: string | null;
  bookedAt?: string;
  completedAt?: string;
  payoutStatus?: string;
  createdAt?: string;
  roomUrl?: string | null;   // the video room for this session, once booked
  startsAt?: string | null;  // the real start instant; no cancelling after it
  unreadMessages?: number;   // thread messages from the other side I haven't read
  documentCount?: number;    // sides attached to this booking
}

// What needs this person on the reader side right now (header pill, readers-page banner).
export interface ReaderAttention { askedByName: number; openRequests: number; readersWaiting: number; unreadMessages: number; total: number }
export async function fetchReaderAttention(): Promise<ReaderAttention | null> {
  try {
    const res = await fetch('/api/reader/bookings/attention', { headers: headers() });
    return res.ok ? res.json() : null;
  } catch { return null; }
}

// ---- Booking thread (messages + sides) ----
export interface ThreadMessage { id: string; byUserId: string; byName: string; text: string; at: string; mine: boolean }
export interface ThreadDocument { id: string; name: string; size: number; byUserId: string; byName: string; at: string; url: string; mine: boolean }
export interface BookingThread { messages: ThreadMessage[]; documents: ThreadDocument[]; unread: number }

const jsonHeaders = () => ({ 'Content-Type': 'application/json', ...(headers() as Record<string, string>) });

export async function fetchBookingThread(id: string): Promise<BookingThread> {
  const res = await fetch(`/api/reader/bookings/${id}/thread`, { headers: headers() });
  if (!res.ok) throw new Error('Could not load messages');
  return res.json();
}
export async function sendBookingMessage(id: string, text: string): Promise<BookingThread> {
  const res = await fetch(`/api/reader/bookings/${id}/messages`, { method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ text }) });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Could not send');
  return res.json();
}
export async function markBookingThreadRead(id: string): Promise<void> {
  await fetch(`/api/reader/bookings/${id}/thread/read`, { method: 'POST', headers: headers() }).catch(() => {});
}
export async function uploadBookingDocument(id: string, file: File): Promise<BookingThread> {
  const u = await fetch(`/api/reader/bookings/${id}/documents/upload-url`, { method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ fileName: file.name, fileType: file.type, size: file.size }) });
  const ud = await u.json().catch(() => ({}));
  if (!u.ok) throw new Error(ud.error || 'Could not start the upload');
  const put = await fetch(ud.uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type || 'application/octet-stream' } });
  if (!put.ok) throw new Error('Upload failed');
  const res = await fetch(`/api/reader/bookings/${id}/documents`, { method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ name: file.name, key: ud.key, size: file.size }) });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Could not attach');
  return res.json();
}
export async function removeBookingDocument(id: string, docId: string): Promise<BookingThread> {
  const res = await fetch(`/api/reader/bookings/${id}/documents/${docId}`, { method: 'DELETE', headers: headers() });
  if (!res.ok) throw new Error('Could not remove');
  return res.json();
}

// Talent creates a request — NO payment. It fans out to the roster; readers
// signal availability; the talent then books whoever they want.
export async function requestReaderBooking(
  input: ReaderBookingRequestInput,
): Promise<{ success: boolean; bookingId: string }> {
  const res = await fetch('/api/reader/bookings/request', {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || data.error || 'Failed to send request');
  }
  return res.json();
}

// Book ONE named reader outright and pay now — no broadcast, no waiting for
// strangers to raise a hand. Used when the talent picked this person (and, with
// Calendly, one of their actual open slots) from their profile.
export async function directBookReader(
  input: ReaderBookingRequestInput & { readerId: string },
): Promise<{ checkoutUrl: string; sessionId: string; bookingId: string; totalCents: number }> {
  const res = await fetch('/api/reader/bookings/direct', {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || data.error || 'Failed to start booking');
  }
  return res.json();
}

// Reader pool — open requests this reader can accept (no talent PII). Throws
// 'not_a_reader' if the caller isn't a reader on casting.
export async function fetchOpenReaderBookings(): Promise<ReaderBooking[]> {
  const res = await fetch('/api/reader/bookings/open', { headers: headers() });
  if (res.status === 403) throw new Error('not_a_reader');
  if (!res.ok) throw new Error('Failed to load open requests');
  const data = await res.json();
  return data.bookings || [];
}

export interface BookingTake {
  id: string;
  number: number;
  mode: string;
  startedAt?: string;
  durationSec: number;
  status: 'ready' | 'encoding' | 'raw';
  s3Key: string | null;
  url: string | null;
  rawUrl: string | null;
  thumbnailUrl: string | null;
  // This viewer's own enhanced copy from the edit drawer, if they made one.
  enhanced: { s3Key: string; url: string; at?: string } | null;
}

// Remember the enhanced copy the edit drawer produced for this take (per viewer).
export async function saveBookingTakeEnhanced(bookingId: string, takeId: string, patch: { s3Key: string; muxPlaybackId?: string }): Promise<void> {
  const res = await fetch(`/api/reader/bookings/${bookingId}/takes/${takeId}/enhanced`, {
    method: 'PATCH', headers: headers(), body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Could not save');
}

// The takes recorded in a booked session. Either side of the booking can read them.
export async function fetchBookingTakes(id: string): Promise<BookingTake[]> {
  const res = await fetch(`/api/reader/bookings/${id}/takes`, { headers: headers() });
  if (!res.ok) return [];
  const data = await res.json();
  return data.takes || [];
}

export async function fetchMyReaderBookings(): Promise<ReaderBooking[]> {
  const res = await fetch('/api/reader/bookings/mine', { headers: headers() });
  if (!res.ok) throw new Error('Failed to load your bookings');
  const data = await res.json();
  return data.bookings || [];
}

// Reader: sessions I've been booked for.
export async function fetchBookedReaderBookings(): Promise<ReaderBooking[]> {
  const res = await fetch('/api/reader/bookings/booked', { headers: headers() });
  if (!res.ok) throw new Error('Failed to load booked sessions');
  const data = await res.json();
  return data.bookings || [];
}

// Reader taps "I'm available". `booked` comes back true when that tap booked
// them outright (card on file, rate at or under what the actor agreed to).
export async function acceptReaderBooking(id: string): Promise<{ success: boolean; booked?: boolean; roomUrl?: string | null }> {
  const res = await fetch(`/api/reader/bookings/${id}/accept`, { method: 'POST', headers: headers() });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to accept');
  }
  return res.json();
}

// Talent books a specific available reader → Stripe Checkout for that reader's rate.
export async function bookReaderBooking(id: string, readerId: string): Promise<{ checkoutUrl: string; sessionId: string }> {
  const res = await fetch(`/api/reader/bookings/${id}/book`, {
    method: 'POST', headers: headers(), body: JSON.stringify({ readerId }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to start booking');
  }
  return res.json();
}

// Reader accepts a paid request: captures the hold and books the session.
export async function confirmReaderBooking(id: string): Promise<{ success: boolean; roomUrl?: string | null }> {
  const res = await fetch(`/api/reader/bookings/${id}/confirm`, { method: 'POST', headers: headers() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || data.error || 'Could not confirm');
  return data;
}

// Reader turns down a paid request. Releases the hold; the talent is never charged.
export async function declineReaderBooking(id: string): Promise<{ success: boolean }> {
  const res = await fetch(`/api/reader/bookings/${id}/decline`, { method: 'POST', headers: headers() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Could not decline');
  return data;
}

// Reader backing out of an ALREADY confirmed session — refunds the talent.
export async function withdrawReaderBooking(id: string): Promise<{ success: boolean }> {
  const res = await fetch(`/api/reader/bookings/${id}/withdraw`, { method: 'POST', headers: headers() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Could not withdraw');
  return data;
}

// Report that the other side didn't turn up, once the ten-minute grace has run.
// `against: 'reader'` refunds the talent; `against: 'talent'` pays the reader for
// the time they held.
export async function reportReaderNoShow(
  id: string, against: 'reader' | 'talent',
): Promise<{ success: boolean; outcome: 'refunded' | 'reader_paid'; payoutStatus?: string }> {
  const res = await fetch(`/api/reader/bookings/${id}/no-show`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(headers() as Record<string, string>) },
    body: JSON.stringify({ against }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || data.error || 'Could not report');
  return data;
}

// Talent gives up the exclusive and puts the request in front of everyone.
export async function askOtherReaders(id: string): Promise<{ success: boolean }> {
  const res = await fetch(`/api/reader/bookings/${id}/ask-others`, { method: 'POST', headers: headers() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Could not open it up');
  return data;
}

export async function completeReaderBooking(id: string): Promise<{ success: boolean; payoutStatus?: string }> {
  const res = await fetch(`/api/reader/bookings/${id}/complete`, { method: 'POST', headers: headers() });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to mark complete');
  }
  return res.json();
}

export async function cancelReaderBooking(id: string): Promise<{ success: boolean }> {
  const res = await fetch(`/api/reader/bookings/${id}/cancel`, { method: 'POST', headers: headers() });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to cancel');
  }
  return res.json();
}

export async function cancelReaderInvite(requestId: string): Promise<any> {
  const res = await fetch(`/api/freelancer/reader/invite/${requestId}`, {
    method: 'DELETE',
    headers: headers(),
  });
  if (!res.ok) throw new Error('Failed to cancel invite');
  return res.json();
}

export async function addDocumentToBooking(requestId: string, filename: string, key: string): Promise<any> {
  const res = await fetch(`/api/freelancer/request/${requestId}/document`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ filename, key }),
  });
  if (!res.ok) throw new Error('Failed to upload document');
  return res.json();
}

export async function removeDocumentFromBooking(requestId: string, documentId: string): Promise<any> {
  const res = await fetch(`/api/freelancer/request/${requestId}/document/${documentId}`, {
    method: 'DELETE',
    headers: headers(),
  });
  if (!res.ok) throw new Error('Failed to remove document');
  return res.json();
}

// ---- Native reader profile (Plus-owned) ------------------------------------
// Reader profiles live in Plus now — casting's reader marketplace was deleted
// on 2026-08-15 and its endpoints return nothing. Readers edit their own.

export type ReaderProfile = {
  userId: string;
  name: string;
  photoUrl: string | null;
  rateCents: number | null;
  directorRateCents: number | null;
  coachRateCents: number | null;
  categories: string[];
  experience: string;
  availabilityNotes: string;
  timezone: string | null;
  calendlyUrl?: string | null;
  published: boolean;
  // Listing gate: a complete application lists the reader; the team can revoke.
  approved?: boolean;
  wantsListed?: boolean;
  revoked?: boolean;
  application?: ReaderApplication | null;
  email?: string | null;
};

export interface ReaderApplication {
  background: string[];
  backgroundOther: string;
  yearsExperience: string;
  professionalExperience: string;
  readingExperience: string;
  strongReader: string;
}
export const READER_APP_BACKGROUND = ['Professional Actor', 'Professional Reader / Scene Partner', 'Casting Professional', 'Director', 'Acting Teacher', 'Other'];
export const READER_APP_YEARS = ['Less than 1 year', '1–2 years', '3–5 years', '5–10 years', '10+ years'];
export const READER_APP_READING = ['I read regularly', 'I have substantial experience', 'I have some experience', 'I’m relatively new to reading'];
export const emptyReaderApplication = (): ReaderApplication => ({ background: [], backgroundOther: '', yearsExperience: '', professionalExperience: '', readingExperience: '', strongReader: '' });
export const readerApplicationComplete = (a: ReaderApplication) =>
  a.background.length > 0 && !!a.yearsExperience && !!a.professionalExperience.trim() && !!a.readingExperience && !!a.strongReader.trim();

export async function getMyReaderProfile(): Promise<{ profile: ReaderProfile | null; complete: boolean }> {
  const res = await fetch('/api/reader/profile', { headers: headers() });
  if (!res.ok) throw new Error('Could not load your reader profile.');
  return res.json();
}

// Presigned PUT for the reader's own headshot. Returns the S3 key, which is
// what photoUrl stores.
export async function getReaderPhotoUploadUrl(
  fileName: string, fileType: string,
): Promise<{ uploadUrl: string; key: string }> {
  const res = await fetch('/api/reader/profile/photo-url', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(headers() as Record<string, string>) },
    body: JSON.stringify({ fileName, fileType }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || 'Could not start the upload.');
  return data;
}

export async function saveMyReaderProfile(
  patch: Partial<ReaderProfile>
): Promise<{ saved: boolean; complete: boolean; published: boolean; approved?: boolean; applicationComplete?: boolean; pendingApproval?: boolean; notice?: string }> {
  const res = await fetch('/api/reader/profile', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...(headers() as Record<string, string>) },
    body: JSON.stringify(patch),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || 'Could not save your profile.');
  return data;
}

// ---- Calendly-backed availability ------------------------------------------
// Read-only. Booking still happens in Hey Joe; this only stops talent guessing
// at a time. Soft-fails to `available: false` — never throws at the caller.

export type ReaderSlotDay = { date: string; slots: string[] };
export type ReaderEventType = {
  uuid: string;
  name: string;
  slug: string | null;
  durationMins: number | null;
  blocks: number | null;
  baseCents: number | null;
  readerTimezone: string | null;
  days: ReaderSlotDay[];
};
export type ReaderAvailability = {
  available: boolean;
  reason?: string;
  timezone?: string;
  calendlyUrl?: string | null;
  eventTypes: ReaderEventType[];
};

export async function fetchReaderAvailability(
  userId: string,
  timezone: string,
  days = 14
): Promise<ReaderAvailability> {
  try {
    const res = await fetch(
      `/api/readers/${encodeURIComponent(userId)}/availability?timezone=${encodeURIComponent(timezone)}&days=${days}`
    );
    if (!res.ok) return { available: false, reason: 'error', eventTypes: [] };
    return await res.json();
  } catch {
    return { available: false, reason: 'error', eventTypes: [] };
  }
}
